<template>
  <f7-page class="main-frm-repair-parts-create-part">
    <template #fixed>
      <TheNavbar
        nav-title="Nueva orden y parte"
        :back-btn-enable="true"
        class="at-navbar secondary-navbar"
      />
    </template>

    <div class="display-flex justify-content-end margin-horizontal margin-top">
      <span class="margin-left-half margin-right">
        Reparación en cliente
      </span>
      <div>
        <f7-toggle
          :checked="clientRepair"
          @toggle:change="onClientRepairChecked"
        />
      </div>
    </div>

    <!-- Step 1: Search customer -->
    <f7-block>
      <UCSearchEntityERP
        :title="$t('common.customer')"
        :placeholder="$t('searchEntity.client.placeholder.searchByNameOrCode')"
        :search-text="$t('searchEntity.client.searchText')"
        :search-cancellation-text="$t('searchEntity.client.searchCancellationText')"
        :search-not-found-text="$t('searchEntity.client.searchNotFoundText')"
        :end-point-for-code-search="fetchClients"
        :end-point-for-list="fetchClients"
        :min-text-search="2"
        :property-entity-shown-input="['Codigo', 'Sujeto.NombreCompleto']"
        :property-entity-shown-list-title="['Codigo']"
        :property-entity-shown-list-subtitle="['Sujeto.NombreCompleto']"
        :autofocus-on-deselection="true"
        @selection="onClientSelection"
      />
    </f7-block>

    <!-- Step 2.1: Select address (clientRepair is true) -->
    <f7-block>
      <UCSearchEntityERP
        ref="searchAddressRef"
        :title="$t('searchEntity.address.title.clientAddress')"
        :placeholder="$t('searchEntity.address.placeholder.searchByAddress')"
        :search-text="$t('searchEntity.address.searchText')"
        :search-cancellation-text="$t('searchEntity.address.searchCancellationText')"
        :search-not-found-text="$t('searchEntity.address.searchNotFoundText')"
        :default-etities-list="selectedClient ? selectedClient.Sujeto.Direcciones : null"
        :property-entity-shown-input="['Id', 'Direccion']"
        :property-entity-shown-list-title="['Id']"
        :property-entity-shown-list-subtitle="['Direccion']"
        :disabled="!selectedClient || !clientRepair"
        :emit-evet-change-default-value="true"
        :autofocus-on-deselection="true"
        @selection="onAddressSelection"
      />
    </f7-block>

    <!-- Step 2.2: Select production line (clientRepair is false) -->
    <f7-block>
      <UCSearchEntityERP
        ref="searchProductionLineRef"
        :title="$t('common.productionLine')"
        :placeholder="$t('searchEntity.common.searchByCodeOrName')"
        :search-text="$t('searchEntity.line.searchText')"
        :search-cancellation-text="$t('searchEntity.line.searchCancellationText')"
        :search-not-found-text="$t('searchEntity.line.searchNotFoundText')"
        :default-etities-list="getLineasProduccion"
        :property-entity-shown-input="['Codigo', 'Nombre']"
        :property-entity-shown-list-title="['Codigo']"
        :property-entity-shown-list-subtitle="['Nombre']"
        :disabled="!selectedClient || clientRepair"
        :emit-evet-change-default-value="true"
        :autofocus-on-deselection="true"
        @selection="onProductionLineSelection"
      />
    </f7-block>

    <!-- Step 3: Select production activity -->
    <f7-block>
      <UCSearchEntityERP
        ref="searchProductionActivityRef"
        :title="$t('searchEntity.productionActivity.title.productionActivity')"
        :placeholder="$t('searchEntity.common.searchByCodeOrName')"
        :search-text="$t('searchEntity.productionActivity.searchText')"
        :search-cancellation-text="$t('searchEntity.productionActivity.searchCancellationText')"
        :search-not-found-text="$t('searchEntity.productionActivity.searchNotFoundText')"
        :end-point-for-code-search="fetchProductionActivities"
        :end-point-for-list="fetchProductionActivities"
        :property-entity-shown-input="['Codigo', 'Nombre']"
        :property-entity-shown-list-title="['Codigo']"
        :property-entity-shown-list-subtitle="['Nombre']"
        :disabled="!selectedClient || !selectedAddress && !selectedProductionLine"
        :emit-evet-change-default-value="true"
        :autofocus-on-deselection="true"
        @selection="onProductionActivitySelection"
      />
    </f7-block>

    <!-- Step 4: Search article -->
    <f7-block>
      <UCSearchEntityERP
        ref="searchArticleRef"
        :title="$t('common.article')"
        :placeholder="$t('searchEntity.article.placeholder.searchByNameOrCode')"
        :search-text="$t('searchEntity.article.searchText')"
        :search-cancellation-text="$t('searchEntity.article.searchCancellationText')"
        :search-not-found-text="$t('searchEntity.article.searchNotFoundText')"
        :end-point-for-code-search="fetchArticles"
        :end-point-for-list="fetchArticles"
        :property-entity-shown-input="['Codigo', 'Nombre']"
        :property-entity-shown-list-title="['Codigo']"
        :property-entity-shown-list-subtitle="['Nombre']"
        :disabled="!selectedClient || !selectedAddress && !selectedProductionLine || !selectedProductionActivity"
        :emit-evet-change-default-value="true"
        :autofocus-on-deselection="true"
        @selection="onArticleSelection"
      />
    </f7-block>

    <!-- Step 5: Search serial number -->
    <f7-block>
      <UCSearchEntityERP
        ref="searchSerialNumberRef"
        :title="$t('searchEntity.serialNumber.title.serialNumber')"
        :placeholder="$t('searchEntity.serialNumber.placeholder.searchBySerialNumber')"
        :search-text="$t('searchEntity.serialNumber.searchText')"
        :search-cancellation-text="$t('searchEntity.serialNumber.searchCancellationText')"
        :search-not-found-text="$t('searchEntity.serialNumber.searchNotFoundText')"
        :end-point-for-code-search="loadSerialNumbers"
        :end-point-for-list="loadSerialNumbers"
        :property-entity-shown-input="['NumeroSerie', 'InfoProducto.Codigo', 'InfoProducto.Nombre']"
        :property-entity-shown-list-title="['NumeroSerie', 'InfoProducto.Codigo']"
        :property-entity-shown-list-subtitle="['InfoProducto.Nombre']"
        :disabled="!selectedClient || !selectedAddress && !selectedProductionLine || !selectedProductionActivity"
        :emit-evet-change-default-value="true"
        :autofocus-on-deselection="true"
        @selection="onSerialNumberSelection"
      />
    </f7-block>

    <!-- Step 6: Select date and observations -->
    <f7-block>
      <UCDatePicker
        :title="$t('common.date')"
        :disabled="!selectedSerialNumber"
        @onchange="onDateChange"
      />
    </f7-block>

    <f7-block class="content-observations">
      <span class="margin-top-half margin-left-half">
        Observaciones
      </span>
      <textarea
        v-model="observations"
        rows="7"
        :class="{ 'disabled-component': !selectedSerialNumber }"
        :disabled="!selectedSerialNumber"
      />
    </f7-block>

    <f7-block>
      <f7-button
        fill
        type="submit"
        :disabled="!selectedSerialNumber || !selectedDate || clientRepair && !selectedAddress"
        @click="createNewOrderPart"
      >
        Crear orden y parte
      </f7-button>
    </f7-block>
  </f7-page>
</template>

<script setup>
import {
  computed,
  onMounted,
  ref,
  shallowRef,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useRepairParts } from '@/composables/useRepairParts';
import { useAppConfigStore } from '@/js/stores/appConfig';
import { useRepairPartsStore } from '@/js/stores/repairParts';
import { useTraceabilityStore } from '@/js/stores/traceability';
import { useParamRef } from '@erpagroapp/params';

import { ConfirmDialogGH, HiDialog, HiNotification } from '@/js/common/HINotificationSupport';
import { HiDateTimeSupport } from '@/js/common/HIConversionSupport';
import { ProductionLocations } from '@/js/enums/externalRepairParts';
import { f7 } from 'framework7-vue';
import { logError } from '@/js/utils/log';
import { callCreateArticleSerialNumber } from '@/js/services/APIErp';
import {
  callCreateOrderAndPartForClient,
  callFetchArticleSerialNumberByArticleAndSerialNumber,
  callFetchArticlesWithSerialNumberRequestByComponent,
  callFetchClients,
  callFetchProductionActivities,
  callFetchSerialNumbersByClient,
} from '@/js/services/repairs/ApiRepairs';
import { Componente } from '@/js/common/enum';

import TheNavbar from '@/components/TheNavbar.vue';
import UCSearchEntityERP from '@/components/common/UCSearchEntityERP.vue';
import UCDatePicker from '@/components/common/UCDatePicker.vue';

/** @import { Ref, ShallowRef } from 'vue' */
/**
 * @import {
 * Articulo,
 * DatosParteReparacion,
 * UnidadContenedora,
 * } from '@erpagroapp/types' */

/** Stores if the repair part is outside */
const clientRepair = ref(true);

/** @type {Ref<Record<string, any> | null>} */
const selectedClient = ref(null);
/** @type {Ref<Record<string, any> | null>} */
const selectedAddress = ref(null);
/** @type {Ref<Record<string, any> | null>} */
const selectedProductionLine = ref(null);
/** @type {Ref<Record<string, any> | null>} */
const selectedProductionActivity = ref(null);
/** @type {Ref<Articulo | null>} */
const selectedArticle = ref(null);
/** @type {Ref<UnidadContenedora | null>} */
const selectedSerialNumber = ref(null);
/** @type {Ref<string>} */
const observations = ref('');
/** @type {Ref<Date | null>} */
const selectedDate = ref(null);

/** @type {ShallowRef<InstanceType<typeof UCSearchEntityERP> | null>} */
const searchProductionLineRef = shallowRef(null);
/** @type {ShallowRef<InstanceType<typeof UCSearchEntityERP> | null>} */
const searchProductionActivityRef = shallowRef(null);
/** @type {ShallowRef<InstanceType<typeof UCSearchEntityERP> | null>} */
const searchArticleRef = shallowRef(null);
/** @type {ShallowRef<InstanceType<typeof UCSearchEntityERP> | null>} */
const searchSerialNumberRef = shallowRef(null);
/** @type {ShallowRef<InstanceType<typeof UCSearchEntityERP> | null>} */
const searchAddressRef = shallowRef(null);

const { userEnviroment } = storeToRefs(useAppConfigStore());
const { loadWorkParts } = useRepairParts();
const {
  worker,
} = storeToRefs(useRepairPartsStore());

const {
  getLineasProduccion,
} = storeToRefs(useTraceabilityStore());

const defaultRepairsLocation = useParamRef('TipoLugarReparacionPorDefecto');
const defaultProductionLine = useParamRef('LineaProduccionDefectoReparaciones');
const defaultProductionActivity = useParamRef('ActividadProduccionDefectoReparaciones');

/** A computed prop that checks if the default production location for user is outside the workshop */
const defaultLocationOutside = computed(() => defaultRepairsLocation.value === ProductionLocations.ADDRESS);

/**
 * A method that is called when user searches to fetch results.
 * @param {string} value - The search value
 */
async function fetchClients(value) {
  if (!userEnviroment.value) {
    return null;
  }

  try {
    return await callFetchClients({
      filter: value ? `q:${value}` : '',
      enterpriseGroupId: userEnviroment.value.EntornoDeTrabajo.GrupoEmpresarial.Id,
    });
  } catch (error) {
    logError(error);
    return null;
  }
}

/** A method that fetches the authorized production lines for user */
async function selectProductionLineIfPossible() {
  if (getLineasProduccion.value.length === 1) {
    searchProductionLineRef.value?.SelectEntity(getLineasProduccion.value[0], true);
  } else if (defaultProductionLine.value && getLineasProduccion.value.length > 0) {
    // Set the default production line loaded for this user
    const productionLine = getLineasProduccion.value.find((item) => item.Id === defaultProductionLine.value);
    if (productionLine) {
      searchProductionLineRef.value?.SelectEntity(productionLine, true);
    }
  }
}

/**
 * A method that is called when user changes the 'Reparación en cliente' toggle state.
 * @param {boolean} value - The new state.
 */
async function onClientRepairChecked(value) {
  selectProductionLineIfPossible();
  clientRepair.value = value;
}

/**
 * A method that is called when user searches to fetch production activities.
 * @param {string} [value] - The search value.
 */
async function fetchProductionActivities(value) {
  if (!userEnviroment.value) {
    return null;
  }

  try {
    return await callFetchProductionActivities({
      filter: value ? `q:${value}` : '',
      enterpriseGroupId: userEnviroment.value.EntornoDeTrabajo.GrupoEmpresarial.Id,
    });
  } catch (error) {
    logError(error);
    return null;
  }
}

/** A method that is called to fetch the default production activity for Repairs submodule. */
async function fetchDefaultOrOnlyProductionActivity() {
  try {
    const { data } = await fetchProductionActivities();

    if (Array.isArray(data)) {
      if (data.length === 1) {
        searchProductionActivityRef.value?.SelectEntity(data[0], true);
      } else if (defaultProductionActivity.value) {
        const productionActivity = data.find((item) => item.Id === defaultProductionActivity.value);
        if (productionActivity) {
          searchProductionActivityRef.value?.SelectEntity(productionActivity, true);
        }
      }
    }
  } catch (error) {
    logError(error);
  }
}

/**
 * A method that is called when user searches to fetch articles.
 * @param {string | undefined} filter - The search value.
 */
async function fetchArticlesByFilter(filter) {
  if (!userEnviroment.value) {
    return null;
  }

  const userId = userEnviroment.value.EntornoDeTrabajo.Usuario?.Id;
  if (!userId) {
    HiNotification.Toast('No se está cargado el usuario del entorno de trabajo');
    return null;
  }

  const enterpriseGroupId = userEnviroment.value.EntornoDeTrabajo.GrupoEmpresarial?.Id;
  if (!enterpriseGroupId) {
    HiNotification.Toast('No se está cargado el grupo empresarial del entorno de trabajo');
    return null;
  }

  try {
    return await callFetchArticlesWithSerialNumberRequestByComponent({
      userId,
      enterpriseGroupId,
      component: Componente.Produccion,
      filter,
    });
  } catch (error) {
    logError(error);
    return null;
  }
}

/**
 * A method that is called when user searches to fetch articles.
 * @param {string | undefined} value - The search value.
 */
async function fetchArticles(value) {
  const filter = value ? `q:${value}` : '';
  return fetchArticlesByFilter(filter);
}

/**
 * A method that is called when user selects one serial number from UCSearchEntityERP.
 * @param {UnidadContenedora | null} value - The selected serial number.
 */
function onSerialNumberSelection(value) {
  // Clear the input value when client is changed
  if (!value && selectedSerialNumber.value) {
    selectedSerialNumber.value = value;
    searchSerialNumberRef.value?.Clear();
  } else {
    selectedSerialNumber.value = value;

    if (selectedSerialNumber.value && !selectedArticle.value) {
      selectedArticle.value = selectedSerialNumber.value.InfoProducto;
      searchArticleRef.value?.SelectEntity(selectedArticle.value, true);
    }
  }
}

/**
 * Loads a serial number by article ID and serial number.
 *
 * @param {object} options
 * @param {number} [options.articleId] - The ID of the article.
 * @param {string} options.serialNumber - The serial number.
 * @returns {Promise<UnidadContenedora | null>} The object.
 */
async function loadSerialNumberArticleByArticleAndSerialNumber({ articleId, serialNumber }) {
  const { data } = await callFetchArticleSerialNumberByArticleAndSerialNumber({
    articleId,
    serialNumber,
  });

  return Array.isArray(data) && data.length && data[0]
    ? data[0]
    : null;
}

/**
 * Creates a new article serial number.
 * @param {PartialDeep<UnidadContenedora>} container - The container.
 */
async function createSerialNumber(container) {
  try {
    HiDialog.Preloader(true, `Creando el número de serie ${container.NumeroSerie}...`);

    const { data } = await callCreateArticleSerialNumber(container);

    if (data.Valor === true) {
      HiNotification.Toast('El número de serie se ha creado correctamente');
    } else {
      HiNotification.Toast(data.Mensaje || 'No se ha podido crear el número de serie');
    }
  } catch (error) {
    logError(error);
    HiNotification.Toast('No se ha podido crear el número de serie');
  } finally {
    HiDialog.Preloader(false);
  }
}

/**
 * Fetches serial numbers for the selected client and article.
 * @param {string} [value] - The search value.
 */
async function loadSerialNumbers(value) {
  if (!selectedClient.value) {
    return null;
  }

  if (value) {
    const serialNumberArticle = await loadSerialNumberArticleByArticleAndSerialNumber({
      articleId: selectedArticle.value?.Id,
      serialNumber: value,
    });

    if (!serialNumberArticle) {
      if (!selectedArticle.value) {
        HiNotification.Toast(`El número de serie "${value}" no se ha encontrado. Seleccione un artículo`);
        return null;
      }
      // Hide the preloader of the search component
      HiDialog.Preloader(false);

      const confirmMsg = `El número de serie "<strong>${value}</strong>" no existe. ¿Desea crearlo?`;
      const confirmTitle = 'Número de serie no existe';

      const hasToCreate = await ConfirmDialogGH(confirmMsg, confirmTitle);
      if (!hasToCreate) {
        searchSerialNumberRef.value?.setInputValue('');
        searchSerialNumberRef.value?.SetFocusOnInputElement();
        return null;
      }

      /** @type {Partial<UnidadContenedora>} */
      const container = {
        NumeroSerie: value,
        InfoProducto: selectedArticle.value,
      };

      await createSerialNumber(container);
      return loadSerialNumbers(value);
    }

    if (serialNumberArticle.InfoProducto) {
      selectedArticle.value = serialNumberArticle.InfoProducto;
      searchArticleRef.value?.SelectEntity(selectedArticle.value, true);
    }

    const response = await callFetchSerialNumbersByClient({
      clientId: selectedClient.value.Id,
      articleId: selectedArticle.value?.Id,
      serialNumber: serialNumberArticle.NumeroSerie,
    });

    if (Array.isArray(response.data) && response.data.length > 0) {
      const foundItem = response.data.find((item) => item.NumeroSerie === value);
      response.data = foundItem ? [foundItem] : response.data;
    }

    return response;
  }

  return callFetchSerialNumbersByClient({
    clientId: selectedClient.value.Id,
    articleId: selectedArticle.value?.Id,
  });
}

/**
 * A method that is called when user selects an article from UCSearchEntityERP.
 * @param {Articulo | null} value - The selected article.
 */
function onArticleSelection(value) {
  if (!value && selectedArticle.value) {
    selectedArticle.value = value;
    searchArticleRef.value?.Clear();

    if (selectedSerialNumber.value) {
      onSerialNumberSelection(null);
    }
  } else {
    selectedArticle.value = value;
  }
}

/**
 * A method that is called when user selects a address from UCSearchEntityERP.
 * @param {Record<string, any> | null} value - The selected address.
 */
function onAddressSelection(value) {
  if (!value && selectedAddress.value) {
    selectedAddress.value = value;
    searchAddressRef.value?.Clear();
  } else {
    selectedAddress.value = value;
    if (value) {
      loadSerialNumbers();
    }
  }
}

/**
 * A method that is called when user selects a client from UCSearchEntityERP.
 * @param {Record<string, any> | null} value - The selected client.
 */
function onClientSelection(value) {
  selectedClient.value = value;

  if (!value) {
    if (selectedAddress.value) {
      onAddressSelection(null);
    }
    if (selectedArticle.value) {
      onArticleSelection(null);
    }
    if (selectedSerialNumber.value) {
      onSerialNumberSelection(null);
    }
  }
}

/**
 * A method that is called when user selects a production line from UCSearchEntityERP.
 * @param {Record<string, any> | null} value - The selected production line.
 */
function onProductionLineSelection(value) {
  selectedProductionLine.value = value;
}

/**
 * A method that is called when user selects a production activity from UCSearchEntityERP.
 * @param {Record<string, any> | null} value - The selected production activity.
 */
function onProductionActivitySelection(value) {
  selectedProductionActivity.value = value;
}

/**
 * A method that is called when a date is selected.
 * @param {Date | null} value - The selected date.
 */
function onDateChange(value) {
  selectedDate.value = value;
}

/** A method that prepares repair part data for sending to the endpoint */
function prepareRepairPartData() {
  if (!selectedClient.value) {
    return null;
  }

  /** @type {PartialDeep<DatosParteReparacion>} */
  const repairPartData = {
    ParteProduccion: {
      Fecha: HiDateTimeSupport.FormatDateTime(selectedDate.value, 'YYYY-MM-DD'),
      Descripcion: observations.value,
    },
    Trabajadores: [worker.value],
    Cliente: selectedClient.value,
    ArticuloOrdenProduccion: selectedArticle.value,
    ArticuloNumeroSerie: selectedSerialNumber.value,
    ActividadProduccion: selectedProductionActivity.value,
  };

  if (clientRepair.value) {
    repairPartData.TipoLugarProduccionParaReparaciones = ProductionLocations.ADDRESS;
    repairPartData.Direccion = selectedAddress.value;
  } else {
    repairPartData.TipoLugarProduccionParaReparaciones = ProductionLocations.PRODUCTION_LINE;
    repairPartData.LineaProduccion = selectedProductionLine.value;
  }

  return repairPartData;
}

/** A method that is called when user clicks on 'Create order and part' */
async function createNewOrderPart() {
  const repairPartData = prepareRepairPartData();
  if (!repairPartData) {
    return;
  }

  HiDialog.Preloader(true, 'Creando orden y parte...');

  try {
    const { data } = await callCreateOrderAndPartForClient(repairPartData);

    if (data.Valor === true) {
      f7.views.main.router.back();
      loadWorkParts();
    } else {
      HiNotification.Toast(data.Mensaje || 'No se ha podido crear orden y parte');
    }
  } catch (error) {
    logError(error);
    HiNotification.Toast('No se ha podido crear orden y parte');
  } finally {
    HiDialog.Preloader(false);
  }
}

onMounted(() => {
  selectedDate.value = new Date();
  // It will trigger OnClientRepairChecked
  clientRepair.value = defaultRepairsLocation.value ? defaultLocationOutside.value : true;

  fetchDefaultOrOnlyProductionActivity();
});
</script>

<style scoped lang="scss">
.main-frm-repair-parts-create-part {
  --date-picker-input-height: 38px;
  --date-picker-label-color: var(--f7-block-color);
  --date-picker-label-font-size: var(--f7-block-font-size);
  --date-picker-label-line-height: var(--f7-line-height);
  --date-picker-label-margin: calc(var(--f7-typography-margin) / 2) 0 0 calc(var(--f7-typography-margin) / 2);

  .content-observations {
    textarea {
      width: 100%;
      border: 1px solid var(--his-silver);
      border-radius: 8px;
      background-color: #fff;
      padding-top: calc(var(--f7-typography-padding) / 2);
      padding-left: calc(var(--f7-typography-padding) / 2);
    }
  }
}
</style>
